import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Permission, PunchStatus, PunchType, dayKeyInTimeZone, resolvePreset } from '@construction/shared';
import { attendanceApi, type AttendanceAnomalyItem, type Punch, type Site } from '@/api/endpoints';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  DetailList,
  DetailSection,
  Drawer,
  EmptyState,
  Field,
  FilterBar,
  FormActions,
  Input,
  ListGroup,
  ListRow,
  PageHeader,
  RowActions,
  Spinner,
  TableRow,
} from '@/components/ui';
import { BackfillForm, type BackfillTarget } from '@/features/attendance/BackfillForm';
import { PersonList } from '@/features/attendance/PersonList';
import { usePunchReview } from '@/features/attendance/PunchRow';
import { SessionLine } from '@/features/attendance/SessionLine';
import type { SessionStatus } from '@/features/attendance/day-sessions';
import {
  defaultAttendanceView,
  parseAttendanceView,
  type AttendanceView,
} from '@/features/attendance/default-view';
import { invalidateAttendance } from '@/features/attendance/invalidate';
import { DownloadButton } from '@/features/exports/DownloadButton';
import { RangeField } from '@/features/filters/RangeField';
import { StatusTabs } from '@/features/filters/StatusTabs';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { formatClock } from '@/lib/format';
import { PUNCH_OVERRIDE_KEY, PUNCH_REASON_KEY } from '@/lib/punch-labels';
import { siteDateKey } from '@/lib/site-time';
import { useCurrentSite } from '@/hooks/useCurrentSite';
import { usePermissions } from '@/lib/permissions';

/**
 * 考勤页（CR-025 重做，CR-052 改成页签，CR-082 按示例重做）。
 *
 * **待复核 N ｜ 异常 N ｜ 工时表**（CR-052，客户原话「考勤需要修改成和采购一样的模式」）：
 * - 待复核、异常：**不按日期**，最早的在前。等人处理的东西不该被日期藏起来；
 * - 异常怎么算：上班超过工地的「最长班次」还没打下班卡才算漏打（工地设置里改，默认 16 小时），在岗的不算；
 * - 工时表：区间和下载在这里；≤ 16 天是「人 × 天」的周表（`PersonList`）。
 *
 * CR-082：
 * - 待复核是一张表，**「拒绝 · 通过」直接在行上**；点一行右侧滑出这张卡的详情（距离、理由、说明）；
 * - 异常是一张表，每行画出「07:58 ─── 未打」，「标记已处理 · 补下班卡」在行上，点了在右侧面板里填；
 * - 补录打卡也在右侧面板里（改之前是页面顶上展开一张卡片）；
 * - 客户原话「我需要一眼就能看出来复核问题，异常问题，汇总那点开每个人也是一眼就看出每天的上班打卡下班，和每天时间」。
 */
export function AttendancePage() {
  const { t } = useTranslation();

  // 工地由路由决定（CR-008）。页面不再自己挑工地，也不再有自己的下拉框。
  const { site, siteId: effectiveSiteId } = useCurrentSite();
  const tz = site?.timezone ?? 'America/Los_Angeles';
  const { can } = usePermissions();
  // 补录直接改工时，要「复核与补录打卡」（CR-040：改之前只看考勤的人也有这个按钮）
  const canManage = can(Permission.ATTENDANCE_MANAGE);

  /** 「今天」按**工地时区**算：管理员人在别的时区时，用浏览器的今天会整体错一格 */
  const today = siteDateKey(tz);

  // 默认落在最近两周 —— 双周是这边最常见的结算周期
  const [range, setRange] = useState(() => resolvePreset('lastTwoWeeks', today));
  const [adding, setAdding] = useState(false);

  const exceptions = useQuery({
    queryKey: ['attendance-exceptions', effectiveSiteId],
    queryFn: () => attendanceApi.exceptions(effectiveSiteId!),
    enabled: effectiveSiteId !== null,
  });

  // 页签：`?view=` 优先，没有就按默认落点。落定之后写进地址 —— 处理完最后一条待复核，页签不会自己跳走
  const [params, setParams] = useSearchParams();
  const requested = parseAttendanceView(params.get('view'));
  const counts = exceptions.data
    ? { pending: exceptions.data.pending.length, anomalies: exceptions.data.anomalies.length }
    : exceptions.isError
      ? { pending: 0, anomalies: 0 }
      : undefined;
  const fallback = defaultAttendanceView(canManage, counts);
  const view = requested ?? fallback;

  const setView = useCallback(
    (next: AttendanceView) => {
      setParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set('view', next);
          return nextParams;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  useEffect(() => {
    if (requested === undefined && fallback !== undefined) setView(fallback);
  }, [requested, fallback, setView]);

  return (
    <>
      <PageHeader
        title={t('nav.attendance')}
        description={site?.name}
        action={
          <>
            {/* 考勤表跟着工时表的区间（CR-043） */}
            {view === 'people' && effectiveSiteId !== null && can(Permission.ATTENDANCE_EXPORT) && (
              <DownloadButton
                path={`/sites/${effectiveSiteId}/exports/attendance`}
                query={{ from: range.from, to: range.to }}
                label={t('export.download')}
                action="attendance.export"
              />
            )}
            {site && canManage && (
              <Button onClick={() => setAdding(true)} data-action="attendance.manual">
                {t('attendance.addManual')}
              </Button>
            )}
          </>
        }
      />

      <StatusTabs<AttendanceView>
        tabs={[
          { value: 'pending', label: t('attendance.tabPending'), count: counts?.pending, attention: true },
          { value: 'anomalies', label: t('attendance.tabAnomalies'), count: counts?.anomalies, attention: true },
          { value: 'people', label: t('attendance.tabPeople') },
        ]}
        value={view ?? null}
        onChange={(next) => next && setView(next)}
      />

      <div className="mt-3">
        {view === undefined && (
          <div className="flex justify-center py-16 text-ink-3">
            <Spinner />
          </div>
        )}

        {(view === 'pending' || view === 'anomalies') && (
          <>
            <FilterBar>
              <span className="text-caption text-ink-3">
                {view === 'pending'
                  ? t('attendance.hintPending')
                  : t('attendance.hintAnomalies', { hours: (exceptions.data?.maxShiftMinutes ?? 960) / 60 })}
              </span>
            </FilterBar>

            {exceptions.isLoading && (
              <div className="flex justify-center py-16 text-ink-3">
                <Spinner />
              </div>
            )}
            {exceptions.isError && <ExceptionsError error={exceptions.error} />}

            {exceptions.data && view === 'pending' && <PendingList punches={exceptions.data.pending} timezone={tz} />}
            {exceptions.data && site && view === 'anomalies' && (
              <AnomalyList items={exceptions.data.anomalies} site={site} canManage={canManage} />
            )}
          </>
        )}

        {view === 'people' && effectiveSiteId !== null && (
          <PersonList
            siteId={effectiveSiteId}
            timezone={tz}
            from={range.from}
            to={range.to}
            toolbar={
              // 考勤按周 / 双周看（结算周期），「今日 / 昨日」对它没意义 —— CR-031
              <RangeField
                from={range.from}
                to={range.to}
                today={today}
                presets={['thisWeek', 'lastWeek', 'lastTwoWeeks', 'thisMonth']}
                onChange={setRange}
              />
            }
          />
        )}
      </div>

      {site && canManage && (
        <Drawer open={adding} onClose={() => setAdding(false)} title={t('attendance.addManual')} subtitle={site.name}>
          <BackfillForm site={site} target={{ mode: 'free' }} onClose={() => setAdding(false)} />
        </Drawer>
      )}
    </>
  );
}

function ExceptionsError({ error }: { error: unknown }) {
  const toMessage = useErrorMessage();
  return <Alert>{toMessage(error)}</Alert>;
}

/** 「9/21 周一」：不按日期的列表里，日期得跟着每一行 */
function punchDay(punch: Punch, timezone: string, language: string): string {
  return new Date(punch.occurredAt).toLocaleDateString(language, {
    timeZone: timezone,
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
  });
}

/**
 * 这张卡**为什么要复核**：系统判定（超范围时带上距离）+ 工人选的理由和自己写的说明。
 * 管理员决定通过还是拒绝，要看的就是这两样。工人自写的内容原样显示，不翻译。
 */
function usePunchProblem(punch: Punch) {
  const { t } = useTranslation();
  const verdict = punch.reason ? t(PUNCH_REASON_KEY[punch.reason] ?? 'attendance.reasonUnknown') : null;
  // 没坐标就没距离，不拿 0 充数（约束 9）
  const distance =
    punch.distanceM != null && punch.reason?.startsWith('OUT_OF_RANGE') ? `${Math.round(punch.distanceM)} m` : null;
  const override = punch.overrideReason
    ? PUNCH_OVERRIDE_KEY[punch.overrideReason]
      ? t(PUNCH_OVERRIDE_KEY[punch.overrideReason]!)
      : punch.overrideReason
    : null;
  const workerNote = punch.overrideReasonNote?.trim() || null;
  return {
    verdict: verdict && (distance ? `${verdict} · ${distance}` : verdict),
    override,
    workerNote,
    says: [override, workerNote].filter(Boolean).join(' — '),
  };
}

/** 「待复核」页签：全部待复核的打卡，最早的在前 */
function PendingList({ punches, timezone }: { punches: Punch[]; timezone: string }) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const opened = punches.find((p) => p.id === openId) ?? null;

  if (punches.length === 0) return <EmptyState title={t('attendance.emptyPending')} />;

  return (
    <>
      {/* 窄屏、手机：列表，点一条底部弹出详情，通过 / 拒绝在详情底部 */}
      <ListGroup className="xl:hidden">
        {punches.map((punch) => (
          <PendingListRow key={punch.id} punch={punch} timezone={timezone} onOpen={() => setOpenId(punch.id)} />
        ))}
      </ListGroup>

      <DataTable
        minWidth="56rem"
        columns={[
          { key: 'who', label: t('attendance.worker'), className: 'w-40' },
          { key: 'punch', label: t('attendance.punchColumn'), className: 'w-36' },
          { key: 'problem', label: t('attendance.problemColumn'), className: 'w-52' },
          { key: 'says', label: t('attendance.workerNoteColumn') },
          { key: 'actions', className: 'w-44' },
        ]}
      >
        {punches.map((punch) => (
          <PendingTableRow
            key={punch.id}
            punch={punch}
            timezone={timezone}
            selected={openId === punch.id}
            onOpen={() => setOpenId(punch.id)}
          />
        ))}
      </DataTable>

      <PunchDetail punch={opened} timezone={timezone} onClose={() => setOpenId(null)} />
    </>
  );
}

function PendingTableRow({
  punch,
  timezone,
  selected,
  onOpen,
}: {
  punch: Punch;
  timezone: string;
  selected: boolean;
  onOpen: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { review, error, canReview } = usePunchReview(punch);
  const problem = usePunchProblem(punch);
  const kind = punch.type === PunchType.IN ? t('punch.clockIn') : t('punch.clockOut');

  return (
    <TableRow selected={selected} onOpen={onOpen}>
      <td className="whitespace-nowrap">
        <span className="font-medium text-ink">{punch.user?.name ?? `#${punch.userId}`}</span>
        <span className="ml-2 text-caption tabular-nums text-ink-3">{punchDay(punch, timezone, i18n.language)}</span>
      </td>
      <td className="whitespace-nowrap tabular-nums">
        <span className="mr-1.5 text-ink-3">{kind}</span>
        {formatClock(punch.occurredAt, timezone, i18n.language)}
      </td>
      <td className="whitespace-nowrap">{problem.verdict && <Badge tone="warn">{problem.verdict}</Badge>}</td>
      <td className="truncate">
        {problem.override && <span className="font-medium text-ink">{problem.override}</span>}
        {problem.workerNote && <span className="text-ink-3"> · {problem.workerNote}</span>}
        {error && <span className="ml-2 text-caption text-danger">{error}</span>}
      </td>
      <td className="whitespace-nowrap">
        {canReview && (
          // 右对齐、主动作在最右（按钮位置规矩）
          <RowActions>
            <Button size="sm" variant="dangerOutline" loading={review.isPending} onClick={() => review.mutate(PunchStatus.REJECTED)}>
              {t('attendance.reject')}
            </Button>
            <Button size="sm" variant="secondary" loading={review.isPending} onClick={() => review.mutate(PunchStatus.APPROVED)}>
              {t('attendance.approve')}
            </Button>
          </RowActions>
        )}
      </td>
    </TableRow>
  );
}

function PendingListRow({ punch, timezone, onOpen }: { punch: Punch; timezone: string; onOpen: () => void }) {
  const { t, i18n } = useTranslation();
  const problem = usePunchProblem(punch);
  const kind = punch.type === PunchType.IN ? t('punch.clockIn') : t('punch.clockOut');
  return (
    <ListRow
      onClick={onOpen}
      title={
        <>
          {punch.user?.name ?? `#${punch.userId}`}
          <span className="font-normal text-ink-3">
            {' '}
            · {kind} <span className="tabular-nums">{formatClock(punch.occurredAt, timezone, i18n.language)}</span>
          </span>
        </>
      }
      subtitle={[punchDay(punch, timezone, i18n.language), problem.verdict, problem.override].filter(Boolean).join(' · ')}
    />
  );
}

/** 一张待复核的卡：时间、距离、系统判定、工人说的；底部 拒绝 · 通过 */
function PunchDetail({ punch, timezone, onClose }: { punch: Punch | null; timezone: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  return (
    <Drawer
      open={punch !== null}
      onClose={onClose}
      title={
        punch
          ? `${punch.user?.name ?? `#${punch.userId}`} · ${punch.type === PunchType.IN ? t('punch.clockIn') : t('punch.clockOut')}`
          : ''
      }
      subtitle={punch && punchDay(punch, timezone, i18n.language)}
      footer={punch && <PunchDetailActions punch={punch} onDone={onClose} />}
    >
      {punch && <PunchDetailBody punch={punch} timezone={timezone} />}
    </Drawer>
  );
}

function PunchDetailBody({ punch, timezone }: { punch: Punch; timezone: string }) {
  const { t, i18n } = useTranslation();
  const problem = usePunchProblem(punch);
  const at = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, { timeZone: timezone, month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
  return (
    <div className="flex flex-col gap-5">
      {problem.verdict && (
        <div>
          <Badge tone="warn">{problem.verdict}</Badge>
        </div>
      )}
      <DetailSection title={t('attendance.punchColumn')}>
        <DetailList
          items={[
            [t('attendance.deviceTime'), <span key="o" className="tabular-nums">{at(punch.occurredAt)}</span>],
            [t('attendance.serverTime'), <span key="r" className="tabular-nums">{at(punch.receivedAt)}</span>],
            // 没坐标就没距离（约束 9）
            [t('attendance.distance'), punch.distanceM != null ? `${Math.round(punch.distanceM)} m` : t('attendance.noLocation')],
          ]}
        />
      </DetailSection>
      {problem.says && (
        <DetailSection title={t('attendance.workerNoteColumn')}>
          <DetailList
            items={[
              problem.override && [t('attendance.overrideReason'), problem.override],
              problem.workerNote && [t('attendance.workerNoteLabel'), <span key="n" className="whitespace-pre-wrap">{problem.workerNote}</span>],
            ]}
          />
        </DetailSection>
      )}
    </div>
  );
}

function PunchDetailActions({ punch, onDone }: { punch: Punch; onDone: () => void }) {
  const { t } = useTranslation();
  const { review, error, canReview } = usePunchReview(punch);
  useEffect(() => {
    if (review.isSuccess) onDone();
  }, [review.isSuccess, onDone]);
  if (!canReview) return null;
  return (
    <>
      {error && <span className="mr-auto text-caption text-danger">{error}</span>}
      <Button variant="dangerOutline" loading={review.isPending} onClick={() => review.mutate(PunchStatus.REJECTED)}>
        {t('attendance.reject')}
      </Button>
      <Button loading={review.isPending} onClick={() => review.mutate(PunchStatus.APPROVED)}>
        {t('attendance.approve')}
      </Button>
    </>
  );
}

/** 「异常」页签的一条：缺卡是一张卡，班次过长是上下两张合成一条 */
interface AnomalyEntry {
  key: string;
  reason: AttendanceAnomalyItem['reason'];
  start: Punch | null;
  end: Punch | null;
  minutes?: number;
}

/** 最早的在前；班次过长的上下两张合成一条 */
function toEntries(items: AttendanceAnomalyItem[]): AnomalyEntry[] {
  const sorted = [...items].sort(
    (a, b) => a.punch.userId - b.punch.userId || new Date(a.punch.occurredAt).getTime() - new Date(b.punch.occurredAt).getTime(),
  );
  const entries: AnomalyEntry[] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const item = sorted[i]!;
    const next = sorted[i + 1];
    // 班次过长：服务端把上下两张各报一次，这里合成一条「07:40 ─── 次日 03:15」
    if (
      item.reason === 'SESSION_TOO_LONG' &&
      item.punch.type === PunchType.IN &&
      next?.reason === 'SESSION_TOO_LONG' &&
      next.punch.userId === item.punch.userId &&
      next.punch.type === PunchType.OUT
    ) {
      entries.push({ key: item.punch.id, reason: item.reason, start: item.punch, end: next.punch, minutes: item.minutes });
      i += 1;
      continue;
    }
    entries.push({
      key: item.punch.id,
      reason: item.reason,
      start: item.punch.type === PunchType.IN ? item.punch : null,
      end: item.punch.type === PunchType.OUT ? item.punch : null,
      minutes: item.minutes,
    });
  }
  const at = (e: AnomalyEntry) => new Date((e.start ?? e.end)!.occurredAt).getTime();
  return entries.sort((a, b) => at(a) - at(b));
}

/**
 * 「异常」页签：全部没处理的异常，最早的在前。
 *
 * 每条直接画出上班到下班，缺的那张是红虚框「未打」，一眼知道缺哪张。
 * 两个动作（照 ADP / UKG）：**补卡**是正解、主按钮在最右，按缺什么写「补下班卡 / 补上班卡」；
 * **标记已处理**给补不了的（人已离职、当天实际没来），必须写说明，工时不变，可以撤销。两个都在右侧面板里填。
 */
function AnomalyList({ items, site, canManage }: { items: AttendanceAnomalyItem[]; site: Site; canManage: boolean }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState<{ key: string; mode: 'resolve' | 'backfill' | null } | null>(null);

  if (items.length === 0) return <EmptyState title={t('attendance.emptyAnomalies')} />;

  const entries = toEntries(items);
  const opened = open ? (entries.find((e) => e.key === open.key) ?? null) : null;
  const tz = site.timezone;
  const anchorOf = (entry: AnomalyEntry) => (entry.start ?? entry.end)!;
  const addLabel = (entry: AnomalyEntry) => t(entry.start !== null ? 'attendance.addOut' : 'attendance.addIn');

  return (
    <>
      <ListGroup className="xl:hidden">
        {entries.map((entry) => {
          const anchor = anchorOf(entry);
          return (
            <ListRow
              key={entry.key}
              onClick={() => setOpen({ key: entry.key, mode: null })}
              title={
                <>
                  {anchor.user?.name ?? `#${anchor.userId}`}
                  <span className="font-normal text-danger"> · {t(`attendance.anomaly.${entry.reason}`)}</span>
                </>
              }
              subtitle={punchDay(anchor, tz, i18n.language)}
            />
          );
        })}
      </ListGroup>

      <DataTable
        minWidth="60rem"
        columns={[
          { key: 'who', label: t('attendance.worker'), className: 'w-32' },
          { key: 'day', label: t('attendance.dayColumn'), className: 'w-32' },
          { key: 'kind', label: t('attendance.anomalyColumn'), className: 'w-32' },
          { key: 'line', label: t('attendance.sessionColumn') },
          { key: 'actions', className: 'w-56' },
        ]}
      >
        {entries.map((entry) => {
          const anchor = anchorOf(entry);
          return (
            <TableRow
              key={entry.key}
              selected={open?.key === entry.key}
              onOpen={() => setOpen({ key: entry.key, mode: null })}
            >
              <td className="whitespace-nowrap font-medium text-ink">{anchor.user?.name ?? `#${anchor.userId}`}</td>
              <td className="whitespace-nowrap tabular-nums text-ink-2">{punchDay(anchor, tz, i18n.language)}</td>
              <td className="whitespace-nowrap">
                <Badge tone="danger">{t(`attendance.anomaly.${entry.reason}`)}</Badge>
              </td>
              <td>
                <div className="max-w-md">
                  <EntryLine entry={entry} timezone={tz} />
                </div>
              </td>
              <td className="whitespace-nowrap">
                {canManage && (
                  <RowActions>
                    <Button size="sm" variant="secondary" onClick={() => setOpen({ key: entry.key, mode: 'resolve' })}>
                      {t('attendance.resolve')}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setOpen({ key: entry.key, mode: 'backfill' })}>
                      {addLabel(entry)}
                    </Button>
                  </RowActions>
                )}
              </td>
            </TableRow>
          );
        })}
      </DataTable>

      <Drawer
        open={opened !== null}
        onClose={() => setOpen(null)}
        title={
          opened
            ? `${anchorOf(opened).user?.name ?? `#${anchorOf(opened).userId}`} · ${t(`attendance.anomaly.${opened.reason}`)}`
            : ''
        }
        subtitle={opened && punchDay(anchorOf(opened), tz, i18n.language)}
        footer={
          opened &&
          canManage &&
          open?.mode === null && (
            <>
              <Button variant="secondary" onClick={() => setOpen({ key: opened.key, mode: 'resolve' })}>
                {t('attendance.resolve')}
              </Button>
              <Button onClick={() => setOpen({ key: opened.key, mode: 'backfill' })}>{addLabel(opened)}</Button>
            </>
          )
        }
      >
        {opened && (
          <AnomalyPanel
            entry={opened}
            site={site}
            mode={open?.mode ?? null}
            onCancel={() => setOpen({ key: opened.key, mode: null })}
            onDone={() => setOpen(null)}
          />
        )}
      </Drawer>
    </>
  );
}

function EntryLine({ entry, timezone }: { entry: AnomalyEntry; timezone: string }) {
  const status: SessionStatus =
    entry.reason === 'SESSION_TOO_LONG' ? 'tooLong' : entry.start !== null ? 'missingEnd' : 'missingStart';
  const dayOf = (p: Punch) => dayKeyInTimeZone(new Date(p.occurredAt), timezone);
  return (
    <SessionLine
      timezone={timezone}
      status={status}
      start={entry.start?.occurredAt ?? null}
      end={entry.end?.occurredAt ?? null}
      minutes={entry.minutes ?? null}
      endsNextDay={Boolean(entry.start && entry.end && dayOf(entry.start) !== dayOf(entry.end))}
    />
  );
}

/** 异常的右侧面板：这一段的线 + 标记已处理（写说明）或补卡（BackfillForm） */
function AnomalyPanel({
  entry,
  site,
  mode,
  onCancel,
  onDone,
}: {
  entry: AnomalyEntry;
  site: Site;
  mode: 'resolve' | 'backfill' | null;
  onCancel: () => void;
  onDone: () => void;
}) {
  const culprits = [entry.start, entry.end].filter((p): p is Punch => p !== null);
  const resolveState = useAnomalyResolve(
    culprits.map((p) => p.id),
    onDone,
  );
  const anchor = culprits[0]!;
  // 缺下班卡、班次过长 → 补下班卡；缺上班卡 → 补上班卡
  const addOut = entry.start !== null;
  const target: BackfillTarget =
    entry.start && entry.end && entry.reason === 'SESSION_TOO_LONG'
      ? { mode: 'anomaly', userId: anchor.userId, type: PunchType.OUT, anchor: entry.start, alsoResolve: entry.end }
      : { mode: 'anomaly', userId: anchor.userId, type: addOut ? PunchType.OUT : PunchType.IN, anchor };

  return (
    <div className="flex flex-col gap-5">
      <EntryLine entry={entry} timezone={site.timezone} />
      {mode === 'backfill' && <BackfillForm site={site} target={target} onClose={onDone} />}
      {mode === 'resolve' && <ResolveForm id={`resolve-${anchor.id}`} state={resolveState} onCancel={onCancel} />}
    </div>
  );
}

/** 标记已处理：写说明、提交（班次过长一次标上下两张）、失效查询 */
function useAnomalyResolve(punchIds: string[], onDone: () => void) {
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const resolve = useMutation({
    mutationFn: () => Promise.all(punchIds.map((id) => attendanceApi.resolveAnomaly(id, note.trim()))),
    onSuccess: () => {
      setNote('');
      invalidateAttendance(queryClient);
      onDone();
    },
    onError: (err) => setError(toMessage(err)),
  });

  return { note, setNote, error, resolve };
}

function ResolveForm({
  id,
  state,
  onCancel,
}: {
  id: string;
  state: ReturnType<typeof useAnomalyResolve>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (state.note.trim()) state.resolve.mutate();
      }}
      className="flex flex-col gap-3"
    >
      {state.error && <Alert>{state.error}</Alert>}
      <Field label={t('attendance.resolveNote')} htmlFor={id} required>
        <Input
          id={id}
          value={state.note}
          onChange={(e) => state.setNote(e.target.value)}
          placeholder={t('attendance.resolveNotePlaceholder')}
          maxLength={500}
          required
          autoFocus
        />
      </Field>
      <FormActions
        submitLabel={t('attendance.resolveConfirm')}
        cancelLabel={t('app.cancel')}
        onCancel={onCancel}
        loading={state.resolve.isPending}
      />
    </form>
  );
}
